// src/components/Layout/Header/index.tsx
import { FC, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, App } from 'antd';
import { SunOutlined, MoonOutlined } from '@ant-design/icons';
import useThemeStore from '@/store/useThemeStore';
import SvgIcon from '@/components/SvgIcon';
import { PATHS } from '@/router/paths';
import { toggleThemeWithSync } from '@/utils/themeSync';
import styles from './index.module.scss';

// 需要显示返回按钮的路由配置
const BACK_BUTTON_CONFIG: Record<string, { backPath: string }> = {
  [PATHS.interviewSimulation.analysis]: { backPath: PATHS.interviewSimulation.history },
};

const Header: FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isDarkMode } = useThemeStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isTogglingTheme, setIsTogglingTheme] = useState(false);

  // 检查当前路由是否需要显示返回按钮（支持带参数的路由）
  const getBackConfig = () => {
    // 精确匹配
    if (BACK_BUTTON_CONFIG[location.pathname]) {
      return BACK_BUTTON_CONFIG[location.pathname];
    }
    // 匹配带参数的路由，如 /interview-simulation/analysis/:id
    const analysisPathPattern = /^\/interview-simulation\/analysis(\/.*)?$/;
    if (analysisPathPattern.test(location.pathname)) {
      return { backPath: PATHS.interviewSimulation.history };
    }
    const cvDetailPathPattern = /^\/document-analysis\/cv\/[^/]+$/;
    if (cvDetailPathPattern.test(location.pathname)) {
      return { backPath: PATHS.documentAnalysis.history };
    }
    const clDetailPathPattern = /^\/document-analysis\/cover-letter\/[^/]+$/;
    if (clDetailPathPattern.test(location.pathname)) {
      return { backPath: PATHS.documentAnalysis.history };
    }
    // 从 History 页面跳转到 CoverLetter 生成页面时显示返回按钮
    const state = location.state as { recordId?: number } | null;
    if (location.pathname === PATHS.documentGeneration.coverLetter && state?.recordId) {
      return { backPath: PATHS.documentGeneration.history };
    }
    return null;
  };

  const backConfig = getBackConfig();

  const handleBack = () => {
    if (backConfig) {
      navigate(backConfig.backPath);
    }
  };

  // 主题切换处理函数
  const handleToggleTheme = async () => {
    if (isTogglingTheme) return; // 防止重复点击

    setIsTogglingTheme(true);
    const success = await toggleThemeWithSync();
    setIsTogglingTheme(false);

    if (!success) {
      message.error('Failed to sync theme preference');
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        {backConfig && (
          <button className={styles.backBtn} onClick={handleBack}>
            <SvgIcon name="arrow-left" size={20} />
            <span>{t('common.back')}</span>
          </button>
        )}
      </div>
      <div className={styles.right}>
        <Button className={styles.themeBtn} type="text" icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />} onClick={handleToggleTheme} loading={isTogglingTheme} />
      </div>
    </header>
  );
};

export default Header;
